import Meta from '../../components/Meta'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  GetLaboratoryCollectionPointsByLaboratoryIdDocument,
  GetLaboratoryCollectionPointsByLaboratoryIdQuery,
  GetLaboratoryCollectionPointsByLaboratoryIdQueryVariables,
  useSoftDeleteLaboratoryCollectionPointMutation,
} from '../../generated/urql.administrator'
import { useClient } from 'urql'
import { Badge, Button, ButtonGroup, Col, Modal, Row } from 'react-bootstrap'
import {
  CartPlus,
  PencilSquare,
  XCircle,
  ArrowCounterclockwise,
} from 'react-bootstrap-icons'
import { InfiniteTable } from '../../components/InfiniteTable'
import * as Time from '../../lib/time'
import moment from 'moment'

const BatchSize = 20

const LaboratoryCollectionPoints: FC<{ id: number }> = ({ id }) => {
  // page content
  const pageTitle = 'Punkty pobrań'
  const [laboratoryCollectionPoints, setLaboratoryCollectionPoints] = useState<
    GetLaboratoryCollectionPointsByLaboratoryIdQuery['laboratory_collection_point']
  >([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const data = useMemo(
    () =>
      [...laboratoryCollectionPoints].sort((a, b) => {
        const aDeleted = a.deletedAt ? 1 : 0
        const bDeleted = b.deletedAt ? 1 : 0
        return aDeleted - bDeleted
      }),
    [laboratoryCollectionPoints]
  )
  const client = useClient()
  const navigate = useNavigate()
  const [, softDeleteCollectionPoint] =
    useSoftDeleteLaboratoryCollectionPointMutation()
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean
    title: string
    message: string
    variant: 'danger' | 'success'
    onConfirm: () => Promise<void>
  }>({ show: false, title: '', message: '', variant: 'danger', onConfirm: async () => {} })

  const loadMoreRows = useCallback(
    async ({ refetch = false }: { refetch: boolean } = { refetch: false }) => {
      const { data, error } = await client
        .query<
          GetLaboratoryCollectionPointsByLaboratoryIdQuery,
          GetLaboratoryCollectionPointsByLaboratoryIdQueryVariables
        >(
          GetLaboratoryCollectionPointsByLaboratoryIdDocument,
          { laboratoryId: id, offset: refetch ? 0 : offset, limit: BatchSize },
          { requestPolicy: refetch ? 'network-only' : undefined }
        )
        .toPromise()

      if (error) {
        console.error(error)
        return
      }

      if (data) {
        setLaboratoryCollectionPoints(
          refetch
            ? data.laboratory_collection_point
            : laboratoryCollectionPoints.concat(
                data.laboratory_collection_point
              )
        )
        setOffset(refetch ? BatchSize : offset + BatchSize)
        setCount(
          data.laboratory_collection_point_aggregate.aggregate?.count || 0
        )
      }
    },
    [client, laboratoryCollectionPoints, offset]
  )

  useEffect(() => {
    loadMoreRows({ refetch: true }).then(() => {})
  }, [])

  return (
    <div>
      <Meta title={pageTitle} />
      <div className="mt-3 mb-3 d-flex justify-content-end">
        <ButtonGroup>
          <Button
            className="d-flex align-items-center"
            onClick={() => {
              navigate(`/laboratory/${id}/collection-point`)
            }}
          >
            <CartPlus size={20} />
            &nbsp;Dodaj
          </Button>
        </ButtonGroup>
      </div>
      <InfiniteTable<
        GetLaboratoryCollectionPointsByLaboratoryIdQuery['laboratory_collection_point'][number]
      >
        data={data}
        columns={[
          { Header: 'Id', accessor: 'id' },
          {
            Header: 'Nazwa',
            id: 'name',
            accessor: (rowData) => (
              <span className={rowData.deletedAt ? 'text-muted' : ''}>
                {rowData.name}
              </span>
            ),
          },
          {
            Header: 'Adres',
            accessor: ({ address, postalCode, city, deletedAt }) => (
              <p className={deletedAt ? 'text-muted' : ''}>
                {address}
                <br />
                {city}, {postalCode}
              </p>
            ),
          },
          {
            Header: 'Status',
            id: 'status',
            accessor: (rowData) =>
              rowData.deletedAt ? (
                <Badge bg="secondary">Nieaktywny</Badge>
              ) : (
                <Badge bg="success">Aktywny</Badge>
              ),
          },
          {
            Header: 'Godziny otwarcia',
            id: 'openingHours',
            accessor: ({ openingHours, deletedAt }) => (
              <Col className={deletedAt ? 'text-muted' : ''}>
                {Object.entries(Time.WeekDay2Name).map(([weekDay, dayName]) => (
                  <Row key={weekDay}>
                    <Col className="">
                      <strong>
                        <small>{dayName}</small>
                      </strong>
                    </Col>
                    {openingHours[weekDay as keyof Time.WeekSchedule] && (
                      <Col>
                        <Badge bg="success">
                          {openingHours[weekDay as keyof Time.WeekSchedule][0]}
                        </Badge>{' '}
                        -{' '}
                        <Badge bg="danger">
                          {openingHours[weekDay as keyof Time.WeekSchedule][1]}
                        </Badge>
                      </Col>
                    )}
                    {!openingHours[weekDay as keyof Time.WeekSchedule] && (
                      <Col>
                        <Badge bg="secondary">Zamknięte</Badge>
                      </Col>
                    )}
                  </Row>
                ))}
              </Col>
            ),
          },
          {
            Header: 'Zablokowane dni',
            id: 'specialClosedDays',
            accessor: ({ openingHours }) => (
              <Col>
                {openingHours.specialClosedDays?.length > 0
                  ? openingHours.specialClosedDays.map((day: string) => (
                      <Row key={day}>
                        <Col>
                          <Badge bg="info">
                            {moment(day, 'YYYY-MM-DD', true).isValid()
                              ? moment(day).format('DD.MM.YYYY')
                              : day}
                          </Badge>
                        </Col>
                      </Row>
                    ))
                  : '-'}
              </Col>
            ),
          },
          {
            Header: '',
            id: 'actions',
            accessor: (rowData) => (
              <div className="d-flex justify-content-end">
                <ButtonGroup>
                  <Button
                    variant="primary"
                    onClick={() => {
                      navigate(
                        `/laboratory/${id}/collection-point/${rowData.id}`
                      )
                    }}
                  >
                    <PencilSquare size={18} />
                  </Button>
                  {rowData.deletedAt ? (
                    <Button
                      variant="success"
                      title="Aktywuj"
                      onClick={() => {
                        setConfirmModal({
                          show: true,
                          title: 'Aktywacja punktu pobrań',
                          message: `Czy na pewno chcesz aktywować punkt pobrań "${rowData.name}"?`,
                          variant: 'success',
                          onConfirm: async () => {
                            await softDeleteCollectionPoint({
                              id: rowData.id,
                              deletedAt: null,
                            })
                            loadMoreRows({ refetch: true })
                          },
                        })
                      }}
                    >
                      <ArrowCounterclockwise size={18} />
                    </Button>
                  ) : (
                    <Button
                      variant="danger"
                      title="Dezaktywuj"
                      onClick={() => {
                        setConfirmModal({
                          show: true,
                          title: 'Dezaktywacja punktu pobrań',
                          message: `Czy na pewno chcesz dezaktywować punkt pobrań "${rowData.name}"? Punkt nie będzie widoczny dla klientów.`,
                          variant: 'danger',
                          onConfirm: async () => {
                            await softDeleteCollectionPoint({
                              id: rowData.id,
                              deletedAt: new Date().toISOString(),
                            })
                            loadMoreRows({ refetch: true })
                          },
                        })
                      }}
                    >
                      <XCircle size={18} />
                    </Button>
                  )}
                </ButtonGroup>
              </div>
            ),
          },
        ]}
        loadMoreRows={loadMoreRows}
        hasMore={offset < count}
      />
      <Modal
        show={confirmModal.show}
        onHide={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{confirmModal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmModal.message}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setConfirmModal((prev) => ({ ...prev, show: false }))
            }
          >
            Anuluj
          </Button>
          <Button
            variant={confirmModal.variant}
            onClick={async () => {
              await confirmModal.onConfirm()
              setConfirmModal((prev) => ({ ...prev, show: false }))
            }}
          >
            Potwierdź
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default LaboratoryCollectionPoints
